import React, { useState, useContext, useEffect, useRef, useCallback } from 'react'
import { Form } from 'semantic-ui-react'
import * as R from 'ramda'
import styled from 'styled-components'
import { toast } from 'libs/toast'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'

import { AudienceRuleForm } from 'organisms/AudienceRuleForm'
import { transformAudienceRuleToUiState } from 'domains/email'
import useAxios, { mutate } from 'hooks/useAxios'
import { getSurveyTokenFromId } from 'reducers'
import { Loader } from 'shared/components/Loader'
import { surveySettingContext } from 'pages/SurveySettings/context'
import { CustomerListWidget } from 'organisms/CustomerListWidget/container'
import { csvDownload } from 'libs/csv-download'
import { ImportCustomerContainer } from 'organisms/ImportCustomerButton/container'
import useSaveUserActivityLog, { ACTION_NAME } from 'hooks/useSaveUserActivityLog'
import { TrialWarningMessage } from 'components/TrialWarningMessage'

const BASE_FIELDS = [
  {
    key: 'customer',
    label: 'Customer',
    field: 'email',
    isCoreField: true,
  },
  {
    key: 'company',
    label: 'Company',
    field: 'company',
    isCoreField: true,
  },
  {
    key: 'customer.tag',
    label: 'Tags',
    field: 'customer.tag',
    isCoreField: true,
  },
]

export default function OneoffEmailDeliverySettingContainer() {
  const [audienceRulesState, setAudienceRulesState] = useState()
  const [isCSVDownloading, setIsCSVDownloading] = useState(false)

  const advancedFilterFields = useAxios({
    url: 'reports/preload-advanced-filters/',
  })

  const surveyToken = useSurveyToken()
  const getAudienceRulesApi = useAxios({
    url: `survey/survey/${surveyToken}/survey-sending-rules/`,
  })

  const getAudienceRulesState = useCallback(() => {
    if (R.prop('audience_rules', getAudienceRulesApi.data) && advancedFilterFields.data) {
      const audienceRulesFields = getAudienceRulesFields(advancedFilterFields.data)
      const audienceRulesState = transformAudienceRuleToUiState(
        getAudienceRulesApi.data,
        audienceRulesFields
      )

      return audienceRulesState
    }
  }, [advancedFilterFields.data, getAudienceRulesApi.data])

  useEffect(() => {
    const audienceRulesState = getAudienceRulesState()
    if (audienceRulesState) {
      setAudienceRulesState(R.map(R.over(R.lensProp('field'), R.prop('value')), audienceRulesState))
    }
  }, [getAudienceRulesState])

  const saveAudienceRulesApi = useAxios({
    method: 'post',
    url: `survey/survey/${surveyToken}/survey-sending-rules/`,
    skip: true,
  })

  const buttonRef = useRef()
  useSetCurrentSaveFunction(buttonRef)
  useSetHeaderSaveButtonLoading(saveAudienceRulesApi.loading || !getAudienceRulesApi.data)

  const customersMatchingAudienceRulesApi = useAxios({
    method: 'post',
    url: `survey/survey/${surveyToken}/audience/`,
    skip: true,
  })

  const setIsAudienceRulesChanged = useDisableSendButton(saveAudienceRulesApi.loading)
  // PET-2084: track dirty in a LOCAL flag and sync to the shared context in an
  // effect (not synchronously inside the change handler — see generic-embed).
  const { setContext } = useContext(surveySettingContext)
  const [isFormDirty, setIsFormDirty] = useState(false)
  useEffect(() => {
    setContext({ isSurveyBuilderDirty: isFormDirty })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormDirty])

  const { surveyId } = useParams()
  const saveUserActivityLog = useSaveUserActivityLog()

  function handleSubmit() {
    const data = {
      audience_rules: audienceRulesState,
      survey_id: surveyId,
    }
    if (audienceRulesState === undefined) {
      return toast.error('Please create an audience rule before saving the survey.')
    }
    return saveAudienceRulesApi
      .fetch({ data })
      .then((response) => {
        setIsAudienceRulesChanged(false)
        setIsFormDirty(false)
        mutate(`survey/survey/${surveyToken}/survey-sending-rules/`, response.data, false)
        toast.success('Audience rule settings were successfully updated.')

        saveUserActivityLog({
          action: ACTION_NAME.edit_survey_publish_delivery_page,
          target: `sur_${surveyId}`,
          requestBody: data,
          statusCode: 200,
        })
      })
      .catch((error) => {
        toast.error(
          'Failed to save audience rule settings. Please try again or contact Simplesat support.'
        )

        saveUserActivityLog({
          action: ACTION_NAME.edit_survey_publish_delivery_page,
          target: `sur_${surveyId}`,
          requestBody: data,
          statusCode: error.response.status,
        })
      })
  }

  useEffect(() => {
    if (audienceRulesState) {
      const getCustomersMatchingAudienceRules = customersMatchingAudienceRulesApi.fetch
      getCustomersMatchingAudienceRules({
        data: {
          audience_rules: audienceRulesState,
          show_list: true,
          all: false,
        },
      })
    }
  }, [audienceRulesState, customersMatchingAudienceRulesApi.fetch])

  if (!(advancedFilterFields.data && getAudienceRulesApi.data)) {
    return <Loader withContainer />
  }

  const allFields = R.map(
    (field) => ({
      label: field.label,
      value: field.field,
      key: field.key,
      isCoreField: field.isCoreField,
      field_type: field.field_type,
    }),
    getAudienceRulesFields(advancedFilterFields.data)
  )

  function handleAudienceRulesChange(audienceRules) {
    setIsAudienceRulesChanged(true)
    setIsFormDirty(true)
    setAudienceRulesState(audienceRules)
  }

  function downloadAudienceRulesCSV() {
    setIsCSVDownloading(true)
    customersMatchingAudienceRulesApi
      .fetch({
        data: {
          audience_rules: audienceRulesState,
          show_list: true,
          all: true,
        },
      })
      .then((response) => {
        const customers = response.data.customers.map((row) => ({
          Name: row.name,
          Email: row.email,
          Company: row.company || '',
        }))

        const fileName = 'matching_audience_rules.csv'
        const header = ['Name', 'Email', 'Company']
        csvDownload(customers, fileName, header, { userInputFields: header })
        setIsCSVDownloading(false)
      })
  }

  return (
    <React.Fragment>
      <div className="grid ui stackable">
        <div className="twelve wide column">
          <h3>Sending audience</h3>
          <p>
            Create an audience based on the customers in your Simplesat account.{' '}
            <a href="https://help.simplesat.io/en/articles/2220503-direct-email-surveys#sending-audience" target="_blank" rel="noopener">
              Learn more about audience rules.
            </a>
          </p>
          <TrialWarningMessage />
          <div className="mb4">
            <ImportCustomerContainer />
          </div>
          <Columns>
            <Form onSubmit={handleSubmit} className="mr3" style={{ minWidth: 400 }}>
              <AudienceRuleForm
                rules={getAudienceRulesState()}
                fields={allFields}
                onChange={handleAudienceRulesChange}
                shouldShowAutocomplete
              />
              <button type="submit" hidden ref={buttonRef} />
            </Form>
            <CustomerListWidget
              loading={customersMatchingAudienceRulesApi.loading && !isCSVDownloading}
              customers={customersMatchingAudienceRulesApi.data}
              audienceRules={audienceRulesState}
              downloadAudienceRulesCSV={downloadAudienceRulesCSV}
              isDownloading={isCSVDownloading}
            />
          </Columns>
        </div>
      </div>
    </React.Fragment>
  )
}

function useSetHeaderSaveButtonLoading(loading) {
  const { setContext } = useContext(surveySettingContext)
  useEffect(() => {
    setContext({
      isSubmitting: loading,
    })

    return () => {
      setContext({
        isSubmitting: false,
      })
    }
  }, [loading, setContext])
}

function useSetCurrentSaveFunction(buttonRef) {
  const { setContext } = useContext(surveySettingContext)

  useEffect(() => {
    function saveAudienceRules() {
      if (buttonRef.current) {
        buttonRef.current.click()
      }
    }

    setContext({
      currentSaveFunction: saveAudienceRules,
    })
  }, [buttonRef, setContext])
}

function useSurveyToken() {
  const { surveyId } = useParams()
  return useSelector((state) => getSurveyTokenFromId(state, surveyId))
}

const Columns = styled.div`
  display: flex;
`
function useDisableSendButton(loading) {
  const [isAudienceRulesChange, setIsAudienceRulesChanged] = useState(false)
  const { setContext } = useContext(surveySettingContext)

  useEffect(() => {
    setContext({
      isEmailActionButtonDisable: isAudienceRulesChange || loading,
    })

    return () => {
      setContext({
        isEmailActionButtonDisable: false,
      })
    }
  }, [isAudienceRulesChange, loading, setContext])

  return setIsAudienceRulesChanged
}

function getAudienceRulesFields(advancedFilterFields) {
  return BASE_FIELDS.concat(
    advancedFilterFields.filter(R.propEq('key', 'customer.attribute')).map((record) => {
      return {
        field: record.field,
        label: record.label,
        key: record.key,
        field_type: record.field_type,
      }
    })
  )
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/components/OneoffEmailDelevirySetting/container.js